import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
  Paper,
  TextField,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../theme";
import { GET, ADD, UPDATE } from "../Functions/apiFunction";
import api from "../Data/api";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ExtensionIcon from "@mui/icons-material/Extension";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";

function SubscriptionSettings() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const admin = JSON.parse(sessionStorage.getItem("admin"));
  const token = `Bearer ${admin.token}`;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderDays, setReminderDays] = useState(2);
  const [extensionDays, setExtensionDays] = useState(3);
  const [endingSoon, setEndingSoon] = useState([]);
  const [loadingEndingSoon, setLoadingEndingSoon] = useState(false);
  const [reFetch, setReFetch] = useState(false);

  const [snakbarOpen, setSnakbarOpen] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [alertMsg, setAlertMsg] = useState("");

  const showAlert = (type, msg) => {
    setAlertType(type);
    setAlertMsg(msg);
    setSnakbarOpen(true);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const res = await GET(token, `${api}/get_subscription_settings`);
        if (res?.response === 200) {
          setReminderDays(res.data.reminder_days);
          setExtensionDays(res.data.extension_days);
        }
      } catch (e) {
        showAlert("error", "Failed to load settings");
      }
      setLoading(false);
    };
    fetchSettings();
  }, [reFetch, token]);

  useEffect(() => {
    const fetchEndingSoon = async () => {
      setLoadingEndingSoon(true);
      try {
        const res = await GET(token, `${api}/get_ending_soon_subscriptions`);
        if (res?.response === 200) {
          setEndingSoon(res.data || []);
        }
      } catch (e) {}
      setLoadingEndingSoon(false);
    };
    fetchEndingSoon();
  }, [reFetch, token]);

  const handleSave = async () => {
    if (reminderDays < 1 || reminderDays > 30 || extensionDays < 1 || extensionDays > 30) {
      showAlert("error", "Values must be between 1 and 30");
      return;
    }
    setSaving(true);
    const res = await UPDATE(
      token,
      `${api}/update_subscription_settings`,
      JSON.stringify({ reminder_days: reminderDays, extension_days: extensionDays })
    );
    setSaving(false);
    if (res?.response === 200) {
      showAlert("success", "Settings saved successfully");
      setReFetch((p) => !p);
    } else {
      showAlert("error", "Failed to save settings");
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      // Trigger the artisan command via a dedicated endpoint
      const res = await ADD(token, `${api}/send_subscription_reminders`, JSON.stringify({}));
      if (res?.response === 200) {
        showAlert("success", `Reminders sent to ${res.count ?? 0} user(s)`);
        setReFetch((p) => !p);
      } else {
        showAlert("error", "Failed to send reminders");
      }
    } catch (e) {
      showAlert("error", "Failed to send reminders");
    }
    setSendingReminders(false);
  };

  const getSubTypeName = (type) => {
    switch (parseInt(type)) {
      case 2: return "Weekly";
      case 3: return "Monthly";
      case 4: return "Alt. Days";
      default: return "One-Time";
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Snackbar
        open={snakbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnakbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnakbarOpen(false)} severity={alertType} sx={{ width: "100%" }}>
          {alertMsg}
        </Alert>
      </Snackbar>

      <Typography variant="h3" component="h3" sx={{ mb: 3 }}>
        Subscription Settings
      </Typography>

      {loading ? (
        <Stack alignItems="center" sx={{ mt: 6 }}>
          <CircularProgress color="secondary" />
        </Stack>
      ) : (
        <Stack spacing={3}>
          {/* Settings Cards */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {/* Reminder Days */}
            <Paper
              elevation={3}
              sx={{ flex: 1, p: 3, borderRadius: 2, backgroundColor: colors.primary[400] }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <NotificationsActiveIcon color="secondary" />
                <Typography variant="h5" fontWeight={600}>
                  Reminder Before End
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Send a push notification to users this many days before their subscription ends.
              </Typography>
              <TextField
                label="Days Before End"
                type="number"
                value={reminderDays}
                onChange={(e) => setReminderDays(parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 30 }}
                fullWidth
                color="secondary"
                variant="outlined"
              />
            </Paper>

            {/* Extension Days */}
            <Paper
              elevation={3}
              sx={{ flex: 1, p: 3, borderRadius: 2, backgroundColor: colors.primary[400] }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <ExtensionIcon color="secondary" />
                <Typography variant="h5" fontWeight={600}>
                  Extension Period
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Number of days users can extend their subscription when they forgot to add wallet funds.
                Each user can use this extension only once per subscription.
              </Typography>
              <TextField
                label="Extension Days"
                type="number"
                value={extensionDays}
                onChange={(e) => setExtensionDays(parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 30 }}
                fullWidth
                color="secondary"
                variant="outlined"
              />
            </Paper>
          </Stack>

          {/* Save + Manual Send */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              disabled={saving}
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              sx={{ minWidth: 180 }}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              size="large"
              disabled={sendingReminders}
              onClick={handleSendReminders}
              startIcon={sendingReminders ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              sx={{ minWidth: 220 }}
            >
              {sendingReminders ? "Sending..." : "Send Reminders Now"}
            </Button>

            <Button
              variant="text"
              color="secondary"
              size="large"
              onClick={() => setReFetch((p) => !p)}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Stack>

          <Divider />

          {/* Info box */}
          <Paper
            elevation={1}
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.greenAccent[700]}`,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>How it works:</strong> The server automatically sends reminder notifications{" "}
              <strong>{reminderDays} day(s)</strong> before a subscription ends (runs daily at 9 AM).
              Users who receive the reminder can tap "Extend" in the app to get a{" "}
              <strong>{extensionDays}-day</strong> grace period — giving them time to top up their wallet.
              Each subscription can only be extended once.
            </Typography>
          </Paper>

          {/* Ending Soon Table */}
          <Typography variant="h5" fontWeight={600}>
            Subscriptions Ending Soon ({endingSoon.length})
          </Typography>

          {loadingEndingSoon ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress color="secondary" size={28} />
            </Stack>
          ) : endingSoon.length === 0 ? (
            <Paper
              elevation={1}
              sx={{ p: 3, borderRadius: 2, backgroundColor: colors.primary[400], textAlign: "center" }}
            >
              <Typography color="text.secondary">No subscriptions ending soon</Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, backgroundColor: colors.primary[400] }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Order ID</strong></TableCell>
                    <TableCell><strong>User</strong></TableCell>
                    <TableCell><strong>Product</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Start Date</strong></TableCell>
                    <TableCell><strong>Wallet</strong></TableCell>
                    <TableCell><strong>Extended</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {endingSoon.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>#{row.id}</TableCell>
                      <TableCell>{row.user_name}</TableCell>
                      <TableCell>{row.product_title}</TableCell>
                      <TableCell>
                        <Chip
                          label={getSubTypeName(row.subscription_type)}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.start_date}</TableCell>
                      <TableCell>
                        <Typography
                          color={
                            parseFloat(row.wallet_amount) < parseFloat(row.order_amount)
                              ? "error"
                              : "success.main"
                          }
                          fontWeight={600}
                        >
                          ₹{row.wallet_amount ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.extension_used ? "Yes" : "No"}
                          size="small"
                          color={row.extension_used ? "warning" : "default"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      )}
    </Box>
  );
}

export default SubscriptionSettings;
